// Booking utilities and constants

export const BOOKING_RULES = {
  fromHomepage: {
    'Alpharetta': [0,1,2,3,4,5,6], // Sun-Sat (all days)
    'Cumming': [0,3,4,5,6]         // Wed-Sun (closed Mon-Tue)
  },
  fromRepairEstimator: {
    'Alpharetta': [1,3,5],    // Mon(1), Wed(3), Fri(5)
    'Cumming': [0,4,6]        // Sun(0), Thu(4), Sat(6)
  }
};

export const LOCATION_CONTACTS = {
  'Alpharetta': {
    phone: '(770) 410-1119',
    address: '2150 North Point Cir, Alpharetta, GA 30022',
    hours: 'Mon-Sat: 11AM-8PM, Sun: 12PM-7PM',
    detailedHours: {
      'Monday': '11:00 AM - 8:00 PM',
      'Tuesday': '11:00 AM - 8:00 PM',
      'Wednesday': '11:00 AM - 8:00 PM',
      'Thursday': '11:00 AM - 8:00 PM',
      'Friday': '11:00 AM - 8:00 PM',
      'Saturday': '11:00 AM - 8:00 PM',
      'Sunday': '12:00 PM - 7:00 PM'
    }
  },
  'Cumming': {
    phone: '(943) 221-6631',
    address: '410 Peachtree Pkwy #4114, Cumming, GA 30041',
    hours: 'Wed-Sat: 11AM-5PM, Sun: 12PM-4:30PM (Closed Mon-Tue)',
    detailedHours: {
      'Monday': 'Closed',
      'Tuesday': 'Closed',
      'Wednesday': '11:00 AM - 5:00 PM',
      'Thursday': '11:00 AM - 5:00 PM',
      'Friday': '11:00 AM - 5:00 PM',
      'Saturday': '11:00 AM - 5:00 PM',
      'Sunday': '12:00 PM - 4:30 PM'
    }
  }
};

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const REPAIR_BLACKOUT_RANGES = [
  {
    // December 1st through December 28th (inclusive) every year
    startMonth: 11, // zero-based December
    startDay: 1,
    endMonth: 11,
    endDay: 28
  }
];

const isDateInRepairBlackout = (date) => {
  return REPAIR_BLACKOUT_RANGES.some(range => {
    const start = new Date(date.getFullYear(), range.startMonth, range.startDay);
    const end = new Date(date.getFullYear(), range.endMonth, range.endDay, 23, 59, 59, 999);
    return date >= start && date <= end;
  });
};

const FIXED_MAJOR_HOLIDAYS = [
  { month: 11, day: 25 } // Christmas Day
];

const getNthWeekdayOfMonth = (year, month, weekday, occurrence) => {
  const date = new Date(year, month, 1);
  const offset = (weekday - date.getDay() + 7) % 7;
  date.setDate(1 + offset + (occurrence - 1) * 7);
  return date;
};

const isMajorHoliday = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  // Fixed-date holidays
  if (FIXED_MAJOR_HOLIDAYS.some(holiday => holiday.month === month && holiday.day === day)) {
    return true;
  }

  // Thanksgiving: fourth Thursday in November
  const thanksgiving = getNthWeekdayOfMonth(year, 10, 4, 4);
  if (month === thanksgiving.getMonth() && day === thanksgiving.getDate()) {
    return true;
  }

  return false;
};

export const getAvailableDays = (location, isFromRepair = false) => {
  const context = isFromRepair ? 'fromRepairEstimator' : 'fromHomepage';
  return BOOKING_RULES[context][location] || [];
};

export const getLocationContact = (location) => {
  return LOCATION_CONTACTS[location] || null;
};

export const formatBookingEmail = (formData, repairDetails = null) => {
  const { name, email, phone, location, selectedDate, notes } = formData;
  const locationContact = getLocationContact(location);
  
  // Get the day of week for the selected date
  // Parse date components to avoid timezone issues
  const [year, month, day] = selectedDate.split('-').map(num => parseInt(num, 10));
  const appointmentDate = new Date(year, month - 1, day);
  const dayName = DAY_NAMES[appointmentDate.getDay()];
  const dayHours = locationContact.detailedHours ? locationContact.detailedHours[dayName] : locationContact.hours;
  
  let message = `Customer Information:
- Name: ${name}
- Email: ${email}
- Phone: ${phone}
- Selected Date: ${formatDate(selectedDate)} (${dayName})
- Location: Master Jewelers - ${location}
- Store Hours on ${dayName}: ${dayHours}

`;

  if (repairDetails) {
    message += `Service Details:
- Category: ${repairDetails.category}
- Service: ${repairDetails.service}
- Branch: ${repairDetails.branch || 'N/A'}
- Estimated Price: ${repairDetails.price}

Service Specifications:
${formatServiceAnswers(repairDetails.answers)}

`;
  }

  if (notes) {
    message += `Customer Notes:
${notes}

`;
  }

  message += `Location Contact:
${locationContact.address}
Phone: ${locationContact.phone}
Hours: ${locationContact.hours}`;

  return message;
};

export const formatConfirmationEmail = (formData, repairDetails = null) => {
  const { name, selectedDate, location } = formData;
  const locationContact = getLocationContact(location);
  
  // Get the day of week for the selected date
  // Parse date components to avoid timezone issues
  const [year, month, day] = selectedDate.split('-').map(num => parseInt(num, 10));
  const appointmentDate = new Date(year, month - 1, day);
  const dayName = DAY_NAMES[appointmentDate.getDay()];
  const dayHours = locationContact.detailedHours ? locationContact.detailedHours[dayName] : locationContact.hours;
  
  let confirmationMessage = `Dear ${name},

Thank you for scheduling an appointment with Master Jewelers!

APPOINTMENT DETAILS:
- Date: ${formatDate(selectedDate)} (${dayName})
- Location: Master Jewelers - ${location}
- Address: ${locationContact.address}
- Store Hours on ${dayName}: ${dayHours}

`;

  if (repairDetails) {
    confirmationMessage += `SERVICE INFORMATION:
- Service: ${repairDetails.category} > ${repairDetails.service}${repairDetails.branch ? ` > ${repairDetails.branch}` : ''}
- Estimated Price: ${repairDetails.price}

`;
  }

  confirmationMessage += `IMPORTANT INFORMATION:
- Please arrive 5-10 minutes early
- Bring the jewelry item(s) for service
- Final pricing confirmed upon inspection

TO MODIFY YOUR APPOINTMENT:
Please call us at ${locationContact.phone} during business hours.

LOCATION DETAILS:
Master Jewelers - ${location}
${locationContact.address}
Phone: ${locationContact.phone}
Hours: ${locationContact.hours}

We look forward to seeing you!

Best regards,
Master Jewelers Team`;

  return confirmationMessage;
};

export const formatDate = (dateString) => {
  // Parse date components to avoid timezone issues
  const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
  const date = new Date(year, month - 1, day);
  
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

export const formatServiceAnswers = (answers) => {
  if (!answers || Object.keys(answers).length === 0) {
    return 'No specific selections made';
  }
  
  // Simply format all answers as they are
  return Object.entries(answers)
    .map(([key, value]) => `- ${key.replace(/_/g, ' ')}: ${value}`)
    .join('\n');
};

export const validateBookingForm = (formData) => {
  const errors = {};
  
  if (!formData.name?.trim()) {
    errors.name = 'Name is required';
  }
  
  if (!formData.email?.trim()) {
    errors.email = 'Email is required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    errors.email = 'Please enter a valid email address';
  }
  
  if (!formData.phone?.trim()) {
    errors.phone = 'Phone number is required';
  } else if (!/^[\d\s\-\(\)\+]{10,}$/.test(formData.phone.replace(/\s/g, ''))) {
    errors.phone = 'Please enter a valid phone number';
  }
  
  if (!formData.location) {
    errors.location = 'Please select a location';
  }
  
  if (!formData.selectedDate) {
    errors.selectedDate = 'Please select a date';
  } else {
    const selectedDate = new Date(formData.selectedDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      errors.selectedDate = 'Please select a future date';
    }
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

export const isDateAvailable = (date, location, isFromRepair = false) => {
  const dayOfWeek = date.getDay();
  const availableDays = getAvailableDays(location, isFromRepair);
  if (!availableDays.includes(dayOfWeek)) {
    return false;
  }

  if (isMajorHoliday(date)) {
    return false;
  }

  if (isFromRepair && isDateInRepairBlackout(date)) {
    return false;
  }

  return true;
};

export const getNextAvailableDate = (location, isFromRepair = false) => {
  const today = new Date();
  const availableDays = getAvailableDays(location, isFromRepair);
  
  // Start checking from tomorrow
  for (let i = 1; i <= 14; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    
    if (!availableDays.includes(date.getDay())) {
      continue;
    }

    if (isMajorHoliday(date)) {
      continue;
    }

    if (isFromRepair && isDateInRepairBlackout(date)) {
      continue;
    }

    if (availableDays.includes(date.getDay())) {
      return date.toISOString().split('T')[0]; // Return YYYY-MM-DD format
    }
  }
  
  return null;
};
